import classNames from 'classnames';
import isFunction from 'lodash/isFunction';
import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { UiCheckboxCleanTypes } from 'src/components/UI/checkbox/types';
import { useIdOrRandomString } from 'src/utils/formHelper';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { useCleanHoverErrors } from 'src/components/UI/inputs/useHoverErrors';
import { StateFormInputOptionsType } from 'src/utils/stateForm';
import { getTestId, TestIdAddType } from 'src/utils/testing';
import { useInputCleanBaseHook } from 'src/utils/hooks/inputs';
import { MaterialErrorMessage } from 'src/components/UI/common/MaterialErrorMessage';

export const UiCleanCheckbox: React.FC<UiCheckboxCleanTypes> = memo(
  ({
    formProps: { clearErrors, onChange: formOnChange, register, unregister, getSubscribeProps, setValue },
    name,
    label,
    id: propId,
    disabled,
    required = false,
    className,
    addAfter,
    onChange,
    wrapperClassname,
    labelClassName,
    clean,
    defaultChecked,
    dataTestId,
    validate,
    showHoverError,
    onClickWrapper,
    material,
    labelTooltip,
    errorLabel,
    type = 'checkbox',
    requiredMessage,
    validateName,
    customCheckedValueForInnerUsageOnly,
  }: UiCheckboxCleanTypes) => {
    const inputOptions = useMemo(
      (): StateFormInputOptionsType => ({
        required,
        validate,
        requiredMessage,
        errorLabel,
      }),
      [required, validate, requiredMessage, errorLabel]
    );

    const [value, errors] = useInputCleanBaseHook<boolean | undefined | null>({
      getSubscribeProps,
      name,
      inputOptions,
      unregister,
      register,
      type,
      validateName,
    });

    const hasErrors = !!errors?.length;

    useEffect(() => {
      if (defaultChecked) {
        setValue(name, true);
      }
    }, [defaultChecked, name, setValue]);

    const { errorString, onChangeInputHover } = useCleanHoverErrors({
      name,
      errors,
      clearErrors,
      showAnyError: showHoverError || material,
      validateName,
    });

    const id = useIdOrRandomString(propId);

    const onChangeLocal = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!disabled) {
          formOnChange(name, e.target.checked);

          onChangeInputHover();

          if (isFunction(onChange)) {
            onChange(name, e.target.checked);
          }
        }
      },
      [disabled, formOnChange, name, onChangeInputHover, onChange]
    );

    const baseTestId: TestIdAddType = useMemo(
      () => (dataTestId ? getTestId('checkbox', dataTestId, name) : () => undefined),
      [dataTestId, name]
    );

    const checked =
      customCheckedValueForInnerUsageOnly === undefined ? value || false : customCheckedValueForInnerUsageOnly;

    const component = (
      <>
        <input
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          type="checkbox"
          className={classNames(className, {
            disabled,
            'filled-in': !clean,
          })}
          defaultChecked={defaultChecked}
          onChange={onChangeLocal}
          checked={checked}
          disabled={material && disabled}
          id={id}
          data-testid={baseTestId()}
          data-name={name}
        />
        <UiHoverTooltip text={labelTooltip} noWrapper render={!!labelTooltip}>
          <label
            className={classNames(labelClassName, {
              error: hasErrors,
            })}
            htmlFor={id}
            data-testid={baseTestId('label')}
          >
            {label}
          </label>
        </UiHoverTooltip>
        {addAfter}
        {material && <MaterialErrorMessage error={errorString} />}
      </>
    );

    return (
      <UiHoverTooltip
        text={errorString}
        render={!!errorString && !material}
        id={name}
        tooltipClassName="error-tooltip"
        noWrapper
      >
        {clean ? (
          <div role="presentation" onClick={onClickWrapper} data-testid={baseTestId('wrapper')}>
            {component}
          </div>
        ) : (
          <p
            role="presentation"
            className={classNames(material ? 'checkbox-material custom' : 'checkbox', wrapperClassname, { disabled })}
            onClick={onClickWrapper}
            data-testid={baseTestId('wrapper')}
          >
            {component}
          </p>
        )}
      </UiHoverTooltip>
    );
  }
);
